import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { AREAS } from "@/lib/heat";

const areaLabel = (k) => AREAS.find((a) => a.key === k)?.label || "—";

export const ScoringCard = ({ points, setPoints, refreshKey }) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/scoring").then((r) => setInfo(r.data)).catch(() => {});
  }, [refreshKey]);

  const qf = info?.question_fields || {};
  const matched = Object.keys(qf).length > 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="scoring-card">
      <h2 className="text-xl font-bold text-slate-900">Quiz scoring</h2>
      <p className="mt-1 text-sm text-slate-500">
        Each answer earns points. Each topic has one answer. If the topic belongs to two areas, both areas get the same points. Each area adds up 9 answers, so the most it can score is 36. Point changes are applied to every report the next time you click "Sync now".
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-5">
        {Object.entries(points).map(([label, v]) => (
          <div key={label} className="rounded-xl bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-700 min-h-[2rem]">{label}</div>
            <Input
              data-testid={`points-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              type="number" min="0" max="4" step="1" value={v} className="mt-1 bg-white"
              onChange={(e) => setPoints({ ...points, [label]: e.target.value === "" ? 0 : Number(e.target.value) })}
            />
          </div>
        ))}
      </div>
      {info && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="question-chart">
            <thead className="text-left text-slate-500">
              <tr><th className="py-2 pr-3">Question</th><th className="py-2 pr-3">Main area</th><th className="py-2 pr-3">Also counts toward</th><th className="py-2">Found in GHL</th></tr>
            </thead>
            <tbody>
              {info.questions.map((q) => {
                const f = qf[q.key] || {};
                const ok = f.rate && (!q.secondary || f.confirm);
                return (
                  <tr key={q.key} className="border-t border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-800">{q.label}</td>
                    <td className="py-2 pr-3 text-slate-600">{areaLabel(q.primary)}</td>
                    <td className="py-2 pr-3 text-slate-600">{q.secondary ? areaLabel(q.secondary) : "—"}</td>
                    <td className="py-2">
                      {!matched ? <span className="text-slate-400">Run a sync</span> : ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
